import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
} from '@mui/material';

import { APP_CONFIG } from '../../../../config/app.config';
import { AbstractPreviewData } from '../../services/types';
import WarehouseDataPreviewHeader from './WarehouseDataPreviewHeader';
import WarehouseDataPreviewTable from './WarehouseDataPreviewTable';
import WarehouseDataColumnMappingArea from './WarehouseDataColumnMappingArea';
import WarehouseDataPreviewFooter from './WarehouseDataPreviewFooter';

interface WarehouseDataPreviewProps {
  open: boolean;
  onClose: () => void;
  previewData: AbstractPreviewData;
  onColumnMappingChange: (field: 'ean' | 'quantity', columnName: string) => void;
  onHeaderRowSettingsChange?: (firstRowIsHeader: boolean, headerRowIndex: number) => void;
  onReprocessWithNewHeaderRow?: (file: File, firstRowIsHeader: boolean, headerRowIndex: number) => Promise<void>;
  onSheetSelect?: (sheetName: string) => Promise<void>;
  currentFile?: File | null;
  options?: {
    canChangeHeaderRow?: boolean;
  }
}

const WarehouseDataPreview: React.FC<WarehouseDataPreviewProps> = ({
  open,
  onClose,
  previewData,
  onColumnMappingChange,
  onHeaderRowSettingsChange,
  onReprocessWithNewHeaderRow,
  onSheetSelect,
  currentFile, 
  options
}) => {
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [dragOverTarget, setDragOverTarget] = useState<'ean' | 'quantity' | null>(null);
  const [highlightDestinations, setHighlightDestinations] = useState(false);
  const [headerRowIndex, setHeaderRowIndex] = useState(previewData.headerRowIndex ?? 0);
  const [firstRowIsHeader, setFirstRowIsHeader] = useState(previewData.firstRowIsHeader ?? true);

  // Update local state when previewData changes
  React.useEffect(() => {
    setHeaderRowIndex(previewData.headerRowIndex ?? 0);
    setFirstRowIsHeader(previewData.firstRowIsHeader ?? true);
  }, [previewData.headerRowIndex, previewData.firstRowIsHeader]);

  const handleFirstRowIsHeaderChange = (checked: boolean) => {
    setFirstRowIsHeader(checked);
    // When turning on "first row is header", automatically set header row index to 0
    const newHeaderRowIndex = checked ? 0 : headerRowIndex;
    setHeaderRowIndex(newHeaderRowIndex);
    onHeaderRowSettingsChange?.(checked, newHeaderRowIndex);

    // If we have a current file, reprocess it with the new settings
    if (currentFile && onReprocessWithNewHeaderRow) {
      onReprocessWithNewHeaderRow(currentFile, checked, newHeaderRowIndex);
    }
  };

  const handleHeaderRowIndexChange = (newIndex: number) => {
    setHeaderRowIndex(newIndex);
    onHeaderRowSettingsChange?.(firstRowIsHeader, newIndex);

    // If we have a current file, reprocess it with the new settings
    if (currentFile && onReprocessWithNewHeaderRow) {
      onReprocessWithNewHeaderRow(currentFile, firstRowIsHeader, newIndex);
    }
  };

  const handleDragStart = (e: React.DragEvent, columnName: string) => {
    setDraggedColumn(columnName);
    e.dataTransfer.effectAllowed = 'move';
    // Show both destination columns in mapped state during drag
    setDragOverTarget('ean');
    setTimeout(() => setDragOverTarget('quantity'), 100);
  };

  const handleDragOver = (e: React.DragEvent, target: 'ean' | 'quantity') => {
    e.preventDefault();
    setDragOverTarget(target);
  };

  const handleDragLeave = () => {
    setDragOverTarget(null);
  };

  const handleDrop = (e: React.DragEvent, target: 'ean' | 'quantity') => {
    e.preventDefault();
    if (draggedColumn) {
      onColumnMappingChange(target, draggedColumn);
    }
    setDraggedColumn(null);
    setDragOverTarget(null);
  };

  const handleConfirmMapping = () => {
    // For both file and Monta data, require both EAN and quantity mapping
    if (previewData.columnMapping?.ean && previewData.columnMapping?.quantity) {
      onClose();
    }
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
    setDragOverTarget(null);
  };

  const handleColumnClick = (columnName: string) => {
    setHighlightDestinations(true);
    // Remove highlights after 800ms
    setTimeout(() => {
      setHighlightDestinations(false);
    }, 800);
  };

  const getEanColumnIndex = () => {
    if (!previewData.columnMapping?.ean) return -1;
    return previewData.headers.findIndex(header => header === previewData.columnMapping?.ean);
  };

  const getQuantityColumnIndex = () => {
    if (!previewData.columnMapping?.quantity) return -1;
    return previewData.headers.findIndex(header => header === previewData.columnMapping?.quantity);
  };

  const eanColumnIndex = getEanColumnIndex();
  const quantityColumnIndex = getQuantityColumnIndex();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          height: '80vh',
          maxHeight: '80vh',
          width: '80vw',
          maxWidth: '1800px',
        }
      }}
    >
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Content */}
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', backgroundColor: APP_CONFIG.ui.theme.colors.backgroundTint }}>
          {/* File Preview */}
          <Box sx={{ flex: 1, gap: 0, display: 'flex', flexDirection: 'column', width: '60%' }}>
            <WarehouseDataPreviewHeader
              previewData={previewData}
              firstRowIsHeader={firstRowIsHeader}
              headerRowIndex={headerRowIndex}
              onFirstRowIsHeaderChange={handleFirstRowIsHeaderChange}
              onHeaderRowIndexChange={handleHeaderRowIndexChange}
              onSheetSelect={onSheetSelect}
              canChangeHeaderRow={options?.canChangeHeaderRow}
            />
            <WarehouseDataPreviewTable
              headers={previewData.headers}
              rows={previewData.rows}
              columnMapping={previewData.columnMapping}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onColumnClick={handleColumnClick}
            />
          </Box>

          {/* Mapping Area - Available for both file and Monta data */}
          <WarehouseDataColumnMappingArea
            columnMapping={previewData.columnMapping}
            rows={previewData.rows}
            eanColumnIndex={eanColumnIndex}
            quantityColumnIndex={quantityColumnIndex}
            draggedColumn={draggedColumn}
            dragOverTarget={dragOverTarget}
            highlightDestinations={highlightDestinations}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onColumnMappingChange={onColumnMappingChange}
          />
        </Box>

        <WarehouseDataPreviewFooter
          previewData={previewData}
          onClose={onClose}
          onConfirm={handleConfirmMapping}
        />
      </DialogContent>
    </Dialog>
  );
};

export default WarehouseDataPreview; 